import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { PageHeader } from '@/components/core';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createOvertimeTableColumns } from '../../components/overtime-table-columns/overtime-table-columns';
import { PayoffDialog } from '../../components/payoff-dialog/payoff-dialog';
import { DEFAULT_OVERTIME_ORDER } from '../../graphql/queries';
import { useGetOvertimeList } from '../../hooks/use-overtime';
import { OvertimePagingResult, OvertimeRecord } from '../../types/overtime.types';

export const OvertimeManagerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_OVERTIME_ORDER,
  });
  const [payoffTarget, setPayoffTarget] = useState<OvertimeRecord | null>(null);
  const canPayoff = useHasPermission(HRM_PERMISSIONS.OVERTIME.PAYOFF);

  const { data, isLoading, error } = useGetOvertimeList({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { Overtimes?: OvertimePagingResult })?.Overtimes;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const columns = createOvertimeTableColumns({ t, onPayoff: setPayoffTarget, canPayoff });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('OVERTIME_MANAGER')} />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        onRowClick={(row: OvertimeRecord) => navigate(`/hr/overtime-manager/${row.UserId}`)}
        mobileView="cards"
      />

      <PayoffDialog
        open={Boolean(payoffTarget)}
        onOpenChange={(open) => !open && setPayoffTarget(null)}
        overtime={payoffTarget}
      />
    </div>
  );
};
